/**
 * LineageIndicator Component
 *
 * DC-427: Shows the provenance/lineage of value drivers - which stations
 * contributed to the driver's calculation. This provides transparency
 * into how Bridge AI synthesized findings into business value.
 *
 * The component can display:
 * - A compact inline view showing contributing station icons
 * - An expanded view with full station names and contribution levels
 * - A tooltip with detailed lineage information
 *
 * @version 1.0.0
 * @date 2025-12-31
 */

import React, { useState } from 'react';
import './lineage-indicator.css';

// ============================================================================
// TYPES
// ============================================================================

export type StationId =
  | 'data_quality'
  | 'live_checks'
  | 'org_context'
  | 'ml_readiness'
  | 'ontology_inference';

export type ContributionLevel = 'primary' | 'supporting' | 'minor';

export interface StationContribution {
  stationId: StationId;
  contribution: ContributionLevel;
  findingsCount?: number;
  confidence?: number;
}

export interface LineageIndicatorProps {
  /** Array of contributing stations with their contribution levels */
  sourceStations: StationContribution[] | StationId[];
  /** Display mode: inline (compact), expanded, or tooltip-only */
  mode?: 'inline' | 'expanded' | 'tooltip';
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Show confidence/contribution level */
  showContribution?: boolean;
  /** Show findings count per station */
  showFindingsCount?: boolean;
  /** Maximum stations to show in inline mode before "+" indicator */
  maxInlineStations?: number;
  /** Click handler for a station */
  onStationClick?: (stationId: StationId) => void;
  /** Additional CSS class */
  className?: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

interface StationConfig {
  name: string;
  shortName: string;
  icon: string;
  color: string;
  bgColor: string;
}

const STATION_CONFIG: Record<StationId, StationConfig> = {
  data_quality: {
    name: 'Data Quality',
    shortName: 'DQ',
    icon: '📊',
    color: '#3b82f6',
    bgColor: '#eff6ff',
  },
  live_checks: {
    name: 'FAIR Assessment',
    shortName: 'FAIR',
    icon: '🔍',
    color: '#22c55e',
    bgColor: '#f0fdf4',
  },
  org_context: {
    name: 'Organization Context',
    shortName: 'Org',
    icon: '🏢',
    color: '#8b5cf6',
    bgColor: '#faf5ff',
  },
  ml_readiness: {
    name: 'ML Readiness',
    shortName: 'ML',
    icon: '🤖',
    color: '#f59e0b',
    bgColor: '#fffbeb',
  },
  ontology_inference: {
    name: 'Ontology Inference',
    shortName: 'Onto',
    icon: '🧬',
    color: '#06b6d4',
    bgColor: '#ecfeff',
  },
};

const CONTRIBUTION_CONFIG: Record<ContributionLevel, { label: string; opacity: number; borderWidth: number }> = {
  primary: { label: 'Primary source', opacity: 1, borderWidth: 2 },
  supporting: { label: 'Supporting evidence', opacity: 0.8, borderWidth: 1 },
  minor: { label: 'Minor contribution', opacity: 0.6, borderWidth: 1 },
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Normalize input to StationContribution array
 */
function normalizeStations(
  input: StationContribution[] | StationId[]
): StationContribution[] {
  if (input.length === 0) return [];

  // Check if it's already StationContribution[]
  if (typeof input[0] === 'object' && 'stationId' in input[0]) {
    return input as StationContribution[];
  }

  // Convert StationId[] to StationContribution[]
  return (input as StationId[]).map((stationId, index) => ({
    stationId,
    contribution: index === 0 ? 'primary' : 'supporting' as ContributionLevel,
  }));
}

/**
 * Get station configuration
 */
export function getStationConfig(stationId: StationId): StationConfig {
  return STATION_CONFIG[stationId] || {
    name: 'Unknown',
    shortName: '?',
    icon: '❓',
    color: '#6b7280',
    bgColor: '#f3f4f6',
  };
}

/**
 * Get station color (for use in other components)
 */
export function getStationColor(stationId: StationId): string {
  const config = STATION_CONFIG[stationId];
  return config?.color || '#6b7280';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Single station chip/icon
 */
interface StationChipProps {
  station: StationContribution;
  size: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  showContribution?: boolean;
  onClick?: () => void;
}

const StationChip: React.FC<StationChipProps> = ({
  station,
  size,
  showLabel = false,
  showContribution = false,
  onClick,
}) => {
  const config = getStationConfig(station.stationId);
  const contributionConfig = CONTRIBUTION_CONFIG[station.contribution];
  const isClickable = !!onClick;

  return (
    <span
      className={`
        lineage-station-chip
        lineage-station-chip--${size}
        ${isClickable ? 'lineage-station-chip--clickable' : ''}
      `}
      style={{
        backgroundColor: config.bgColor,
        borderColor: config.color,
        borderWidth: contributionConfig.borderWidth,
        opacity: contributionConfig.opacity,
      }}
      onClick={onClick}
      title={`${config.name}${showContribution ? ` (${contributionConfig.label})` : ''}`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <span className="station-icon">{config.icon}</span>
      {showLabel && <span className="station-label">{config.shortName}</span>}
      {station.findingsCount !== undefined && station.findingsCount > 0 && (
        <span className="station-count">{station.findingsCount}</span>
      )}
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const LineageIndicator: React.FC<LineageIndicatorProps> = ({
  sourceStations,
  mode = 'inline',
  size = 'medium',
  showContribution = false,
  showFindingsCount = false,
  maxInlineStations = 4,
  onStationClick,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const stations = normalizeStations(sourceStations);

  if (stations.length === 0) {
    return (
      <span className={`lineage-indicator lineage-indicator--empty ${className}`}>
        <span className="lineage-empty-text">No source data</span>
      </span>
    );
  }

  // Sort by contribution level (primary first)
  const sortedStations = [...stations].sort((a, b) => {
    const order: Record<ContributionLevel, number> = { primary: 0, supporting: 1, minor: 2 };
    return order[a.contribution] - order[b.contribution];
  });

  // ============================================================================
  // INLINE MODE - Compact horizontal display
  // ============================================================================
  if (mode === 'inline') {
    const visibleStations = sortedStations.slice(0, maxInlineStations);
    const hiddenCount = sortedStations.length - maxInlineStations;

    return (
      <span
        className={`lineage-indicator lineage-indicator--inline lineage-indicator--${size} ${className}`}
        title={`Derived from: ${sortedStations.map(s => getStationConfig(s.stationId).name).join(', ')}`}
      >
        <span className="lineage-label">From:</span>
        <span className="lineage-stations">
          {visibleStations.map((station) => (
            <StationChip
              key={station.stationId}
              station={station}
              size={size}
              showContribution={showContribution}
              onClick={onStationClick ? () => onStationClick(station.stationId) : undefined}
            />
          ))}
          {hiddenCount > 0 && (
            <span
              className="lineage-more"
              onClick={() => setIsExpanded(!isExpanded)}
              title={`${hiddenCount} more stations`}
            >
              +{hiddenCount}
            </span>
          )}
        </span>
      </span>
    );
  }

  // ============================================================================
  // EXPANDED MODE - Full vertical list with details
  // ============================================================================
  if (mode === 'expanded') {
    return (
      <div className={`lineage-indicator lineage-indicator--expanded lineage-indicator--${size} ${className}`}>
        <div className="lineage-header">
          <span className="lineage-title">Evidence Lineage</span>
          <span className="lineage-count">{stations.length} sources</span>
        </div>
        <div className="lineage-list">
          {sortedStations.map((station) => {
            const config = getStationConfig(station.stationId);
            const contributionConfig = CONTRIBUTION_CONFIG[station.contribution];

            return (
              <div
                key={station.stationId}
                className={`lineage-item lineage-item--${station.contribution}`}
                style={{ borderLeftColor: config.color }}
                onClick={onStationClick ? () => onStationClick(station.stationId) : undefined}
                role={onStationClick ? 'button' : undefined}
              >
                <span className="item-icon">{config.icon}</span>
                <div className="item-content">
                  <span className="item-name">{config.name}</span>
                  <span className="item-contribution">{contributionConfig.label}</span>
                </div>
                {showFindingsCount && station.findingsCount !== undefined && (
                  <span className="item-findings">{station.findingsCount} findings</span>
                )}
                {station.confidence !== undefined && (
                  <span className="item-confidence">{Math.round(station.confidence * 100)}%</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ============================================================================
  // TOOLTIP MODE - Just icons with hover tooltip
  // ============================================================================
  return (
    <span
      className={`lineage-indicator lineage-indicator--tooltip lineage-indicator--${size} ${className}`}
      title={`Sources: ${sortedStations.map(s => getStationConfig(s.stationId).name).join(' → ')}`}
    >
      {sortedStations.map((station, index) => (
        <React.Fragment key={station.stationId}>
          <StationChip
            station={station}
            size={size}
            onClick={onStationClick ? () => onStationClick(station.stationId) : undefined}
          />
          {index < sortedStations.length - 1 && (
            <span className="lineage-arrow">→</span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
};

/**
 * Mini lineage indicator - just shows count with tooltip
 */
export const LineageMini: React.FC<{
  sourceCount: number;
  sourceNames?: string[];
  className?: string;
}> = ({ sourceCount, sourceNames, className = '' }) => {
  const tooltip = sourceNames
    ? `Derived from: ${sourceNames.join(', ')}`
    : `Derived from ${sourceCount} data sources`;

  return (
    <span
      className={`lineage-mini ${className}`}
      title={tooltip}
    >
      <span className="lineage-mini-icon">🔗</span>
      <span className="lineage-mini-count">{sourceCount}</span>
    </span>
  );
};

export default LineageIndicator;
